import moment from 'moment';

/**
 * Decide whether a single recurring occurrence should render on the calendar.
 *
 * Recurring messages advance ONE occurrence at a time: when an occurrence fires
 * it is confirmed + soft-deleted and a successor doc is spawned whose `dueDate`
 * is the NEXT unsent occurrence. The calendar expands the (still full) rruleset
 * of the currently-pending doc from its original DTSTART, so without this filter
 * the already-sent occurrences (everything before `dueDate`) keep re-rendering —
 * a sent message that never leaves the calendar.
 *
 * Hides:
 *  - EXDATE-excluded occurrences,
 *  - occurrences before the series DTSTART,
 *  - already-sent occurrences: strictly before the pending doc's `dueDate`.
 *
 * Pure + minute-quantised so it is unit-testable and matches the selector's
 * existing `moment(x).startOf('minute').valueOf()` comparison basis.
 */
export function isRecurringOccurrenceVisible(
  occurrence: Date,
  opts: {
    dtstart?: Date | null;
    dueDate?: Date | string | number | null;
    excludedMinuteValues?: Set<number>;
    /**
     * The occurrence as it will be DRAWN, when that differs from the rule instant.
     *
     * These two are in different time frames and comparing across them silently
     * loses occurrences. EXDATEs and DTSTART are written in the RULE's frame, so
     * those two checks must use `occurrence`. A recurring document's stored
     * `dueDate`, however, is written DST-ADJUSTED (`getNextAdjustedOccurrence` →
     * `adjustForDSTChange`) so the customer keeps their wall-clock time across a
     * DST change — so the already-sent check must use the adjusted instant.
     *
     * Comparing the raw instant against the adjusted dueDate made the NEXT UNSENT
     * occurrence of every series crossing an autumn fall-back look already-sent,
     * and it disappeared from the calendar in the week before it fired.
     *
     * Defaults to `occurrence`, which is correct wherever no adjustment applies.
     */
    displayedOccurrence?: Date;
  },
): boolean {
  const minuteValue = moment(occurrence).startOf('minute').valueOf();

  if (opts.excludedMinuteValues?.has(minuteValue)) return false;
  if (opts.dtstart && moment(occurrence).isBefore(opts.dtstart)) return false;

  if (opts.dueDate != null) {
    const dueMinuteValue = moment(opts.dueDate).startOf('minute').valueOf();
    const sentComparisonValue = moment(opts.displayedOccurrence ?? occurrence).startOf('minute').valueOf();
    if (sentComparisonValue < dueMinuteValue) return false; // already sent
  }

  return true;
}

/** A single-step skip for a not-yet-materialized cycle — the calendar chip case.
 *  Keyed to the occurrence START instant (minute granularity is the BE↔client
 *  contract). Mirrors `PendingStepSkip` in drip-step-skip-state.ts, kept here so
 *  the common-layer selector need not import from the extension layer. */
export interface PendingStepSkipEntry {
  occurrenceDate: Date | string | number;
  stepIndex: number;
}

/**
 * F4 — should `stepIndex` be HIDDEN on the calendar for the occurrence that
 * starts at `occurrenceMinute` (a minute-floored epoch)?
 *
 *   - CURRENT cycle (occurrenceMinute === the drip's own dueDate minute) →
 *     hidden iff the step is in `drip.skippedSteps`. This is the step-card skip;
 *     `skippedSteps` only ever holds current-cycle indices.
 *   - ANY other (future) cycle → hidden iff a `pendingStepSkips` override matches
 *     both the step AND the occurrence minute.
 *
 * Pure + minute-quantised, matching the selector's existing comparison basis, so
 * it is unit-testable in node env rather than through a wired calendar render.
 * Tolerant of junk entries in `pendingStepSkips` (never throws).
 */
export function isDripStepSkippedForOccurrence(opts: {
  stepIndex: number;
  occurrenceMinute: number;
  dueMinute: number | null;
  skippedSteps: number[];
  pendingStepSkips: PendingStepSkipEntry[];
}): boolean {
  const { stepIndex, occurrenceMinute, dueMinute, skippedSteps, pendingStepSkips } = opts;

  if (dueMinute != null && occurrenceMinute === dueMinute && skippedSteps.includes(stepIndex)) {
    return true;
  }

  return pendingStepSkips.some(
    (ps) =>
      !!ps &&
      ps.stepIndex === stepIndex &&
      Math.floor(new Date(ps.occurrenceDate).getTime() / 60000) === occurrenceMinute,
  );
}
